import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { getAppStore } from '../../app/AppStore';
import { CompositeSessionInfoSelectors, SessionServerSelectors } from '../../app/selectors/SessionInfoSelectors';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { ActionBar, ActionBarButton } from '../../common/react/components/ActionBar';
import { cssClass, fillStyle, flexColumnStyle } from '../../style';
import { ServerFilterClauses } from '../../ts/helpers/ServerFilterClauses';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { FlowBaseInfo } from '../../ts/models/FlowInfo';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { Set } from '../../ts/models/Set';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';
import { FlowTaskActions } from '../actions/FlowTaskActions';
import { showResumeRunFlowTasksActionDialog } from '../actions/ResumeRunFlowTasksAction';
import { Flows } from '../resources/Flows';
import { RunFlowTasks } from '../resources/RunFlowTasks';
import { openCreateFlowRunTaskDialog } from '../utils/FlowDialogUtils';
import { FlowRunSchedulesDetailsGrid } from './FlowRunSchedulesDetailsGrid';

const cssPrefix = 'FlowRunSchedulesGridView';

const rootClass = cssClass(cssPrefix, 'root', fillStyle, flexColumnStyle, {
  overflow: 'auto',
  backgroundColor: CommonStyles.Colors.Grey245
});

export type SelectedTasks = { readonly [id: string]: RunFlowTaskInfo };

export type FlowRunSchedulesGridViewProps = {
  readonly flowAllowedActions: FlowAllowedActions;
  readonly flowInfo: FlowBaseInfo;
};

export type FlowRunSchedulesGridViewState = {
  readonly flowTasks: ReadonlyArray<RunFlowTaskInfo>;
  readonly outputs: ReadonlyArray<FlowOutputStepInfo>;
  readonly selectedTasks: SelectedTasks;
};

export class FlowRunSchedulesGridView extends React.PureComponent<FlowRunSchedulesGridViewProps, FlowRunSchedulesGridViewState> {

  private readonly memoizedCreateActionButtons = defaultMemoize(createActionButtons);
  private readonly memoizedCreateActionsMenuItems = defaultMemoize(createActionsMenuItems);

  private unmounted: boolean = false;

  constructor(props: FlowRunSchedulesGridViewProps) {
    super(props);
    this.state = {
      flowTasks: [],
      outputs: [],
      selectedTasks: {}
    };
    this.fetchData();
  }

  public componentDidUpdate(prevProps: Readonly<FlowRunSchedulesGridViewProps>, prevState: Readonly<FlowRunSchedulesGridViewState>) {
    if (this.props.flowInfo.id !== prevProps.flowInfo.id) {
      this.fetchData();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public render(): JSX.Element {
    return (
      <div className={rootClass}>
        {this.renderActionBar()}
        <FlowRunSchedulesDetailsGrid
          anyActionsAllowed={this.props.flowAllowedActions.runFlow}
          flowTasks={this.state.flowTasks}
          outputs={this.state.outputs}
          selectedTasks={this.state.selectedTasks}
          createTaskAllowed={this.props.flowAllowedActions.manageFlow}
          flowFailureThresholdForRunPrevention={SessionServerSelectors.flowFailureThresholdForRunPrevention(getAppStore().getState())}
          isServerOrSiteAdmin={CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(getAppStore().getState())}
          onSelectId={this.selectFlowTaskId}
          onDeselectId={this.deselectFlowTaskId}
          onUpdateTasks={this.fetchRunFlowTasks}
          onResumeAllSuspendedTasks={this.resumeAllSuspendedTasks}
        />
      </div>
    );
  }

  private renderActionBar(): JSX.Element | null {
    if (this.props.flowAllowedActions.manageFlow) {
      const actionButtons = this.memoizedCreateActionButtons(
        this.props.flowInfo,
        this.fetchRunFlowTasks
      );
      const actionsMenuItems = this.memoizedCreateActionsMenuItems(this.state.selectedTasks, this.fetchRunFlowTasks);
      return (
        <ActionBar
          actionButtons={actionButtons}
          actionsMenuItems={actionsMenuItems}
          anyActionsAllowed={this.props.flowAllowedActions.manageFlow}
          selectionSize={Object.keys(this.state.selectedTasks).length}
          selectAll={this.selectAllFlowTasks}
          clear={this.clearTasks}
        />
      );
    } else {
      return null;
    }
  }

  private resumeAllSuspendedTasks = () => {
    const suspendedTasks = this.state.flowTasks.filter((task) => {
      return task.suspended;
    });
    showResumeRunFlowTasksActionDialog(Set.fromIdItemArray(suspendedTasks))
      .then(() => this.fetchRunFlowTasks());
  }

  private fetchData() {
    this.fetchOutputSteps();
    this.fetchRunFlowTasks();
  }

  private fetchOutputSteps = () => {
    Flows.getOutputStepInfos(this.props.flowInfo.id).then(this.updateOutputSteps);
  }

  private fetchRunFlowTasks = () => {
    const runFlowTasks = RunFlowTasks.all().addFilter(ServerFilterClauses.serverFilterClauses.flow(this.props.flowInfo.id));
    SliceableHelper.fetchAll(runFlowTasks).then(tasks => {
      this.setState({
        flowTasks: tasks.items,
        selectedTasks: this.getSelectionAfterUpdate(tasks.items)
      });
    });
  }


  private updateOutputSteps = (outputSteps: ReadonlyArray<FlowOutputStepInfo>) => {
    if (!this.unmounted) {
      this.setState({ outputs: outputSteps });
    }
  }

  private readonly setSelectedTasks = (selectedTasks: SelectedTasks) => {
    if (!this.unmounted) {
      this.setState({ selectedTasks });
    }
  }

  private readonly selectFlowTaskId = (flowTaskId: string) => {
    if (this.state.flowTasks) {
      for (const task of this.state.flowTasks) {
        if (task.id === flowTaskId) {
          this.setSelectedTasks({ ...this.state.selectedTasks, [flowTaskId]: task});
          return;
        }
      }
    }
  }

  private readonly getSelectionAfterUpdate = (flowTasks: ReadonlyArray<RunFlowTaskInfo>) => {
    const selectedTaskIds: string[] = Object.keys(this.state.selectedTasks);
    const newSelectionState: { [id: string]: RunFlowTaskInfo } = {};
    selectedTaskIds.forEach((id) => {
      if (flowTasks.find((task) => task.id === id)) {
        newSelectionState[id] = this.state.selectedTasks[id];
      }
    });
    return newSelectionState;
  }

  private readonly deselectFlowTaskId = (id: string) => {
    const { [id]: removed, ...rest } = this.state.selectedTasks;
    this.setSelectedTasks(rest);
  }

  private readonly selectAllFlowTasks = () => {
    const flowTasks: { [taskId: string]: RunFlowTaskInfo } = {};
    this.state.flowTasks.forEach(task => {
      flowTasks[task.id] = task;
    });
    this.setSelectedTasks(flowTasks);
  }

  private readonly clearTasks = () => {
    this.setSelectedTasks({});
  }
}

function createActionButtons(
  flowInfo: FlowBaseInfo,
  done: () => void
): ReadonlyArray<ActionBarButton> {
  return [{
    label: Messages.newTask(),
    execute: () => {
      openCreateFlowRunTaskDialog(flowInfo).then(done);
    },
  }];
}

function createActionsMenuItems(selectedTasks: SelectedTasks, done: () => void) {
  return FlowTaskActions.getFlowTasksActions(selectedTasks, done);
}
